/**
 * StructuresSystem.js
 *
 * Phase 11 — Renders persistent player-built structures (beacons, crates,
 * auto-miners, refuel pads, turrets) on the planet surface in Babylon.js,
 * plus a ghost preview that follows the player's aim while in build mode.
 *
 * Composed by GameScene: React-side fetches `/api/structures` then pushes the
 * list via `scene.setStructures(list)`. To place a new structure, React
 * enters build mode via `scene.startBuildPreview(typeId)` and the player
 * raycasts to the ground; left-click commits, ESC/B exits.
 *
 * Public API (used by GameScene/Explore3D):
 *   - setStructures(list)              ← refresh after fetch / build
 *   - upsertStructure(doc)
 *   - removeStructure(id)
 *   - startBuildPreview(typeId, spec)  ← spec describes color/footprint
 *   - stopBuildPreview()
 *   - getBuildSnapshot()               ← {active, typeId, valid, point}
 *   - tick(dt)
 *   - getNearStructure(radius)         ← {id,type,distance} for E prompts
 *   - getPois()                        ← radar pings (beacons + any structure)
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
  SpotLight,
} from '@babylonjs/core';

const TYPE_COLOR = {
  beacon: '#fbbf24',
  storage_crate: '#94a3b8',
  auto_miner: '#22d3ee',
  refuel_pad: '#34d399',
  turret: '#f43f5e',
  oxygen_generator: '#38bdf8',
  power_generator: '#facc15',
  floodlight: '#fde68a',
  refinery: '#fb923c',
  trade_terminal: '#a78bfa',
  cargo_silo: '#64748b',
  heavy_turret: '#ef4444',
  habitat: '#5eead4',
};

function hex(h) {
  h = (h || '#ffffff').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return new Color3(r, g, b);
}

export class StructuresSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.getPlayerForward = opts.getPlayerForward || (() => new Vector3(0, 0, 1));
    this.getCameraPos = opts.getCameraPos || (() => this.getPlayerPos());
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);

    this._root = new TransformNode('structuresRoot', scene);
    this._byId = new Map();              // id → { doc, root, meshes, mats }
    this._buildState = {
      active: false,
      typeId: null,
      spec: null,                        // {label, footprint, color, height}
      preview: null,                     // mesh root
      previewMats: [],
      validPoint: null,                  // Vector3
      validRotationY: 0,
    };
  }

  // ============ Persisted structures ============

  setStructures(list) {
    const incoming = new Map();
    for (const d of (list || [])) incoming.set(d.id, d);
    // Remove any structures no longer in the list
    for (const id of Array.from(this._byId.keys())) {
      if (!incoming.has(id)) this.removeStructure(id);
    }
    // Upsert
    for (const d of incoming.values()) this.upsertStructure(d);
  }

  upsertStructure(doc) {
    if (!doc?.id) return;
    const existing = this._byId.get(doc.id);
    if (existing) {
      // Just refresh transform if it moved (unusual but safe).
      existing.doc = doc;
      existing.root.position.set(doc.pos.x, this._sampleY(doc.pos.x, doc.pos.z), doc.pos.z);
      existing.root.rotation.y = doc.rotation_y || 0;
      return;
    }
    const entry = this._buildStructureMesh(doc);
    this._byId.set(doc.id, entry);
  }

  removeStructure(id) {
    const entry = this._byId.get(id);
    if (!entry) return;
    try { entry.root.dispose(false, true); } catch (_e) { /* noop */ }
    this._byId.delete(id);
  }

  clear() {
    for (const id of Array.from(this._byId.keys())) this.removeStructure(id);
    this.stopBuildPreview();
  }

  // ============ Build preview ============

  startBuildPreview(typeId) {
    this.stopBuildPreview();
    const spec = this._specFor(typeId);
    if (!spec) return;
    const root = new TransformNode(`buildGhost_${typeId}`, this.scene);
    const meshes = this._spawnSilhouette(spec, root, /* preview */ true);
    this._buildState = {
      active: true,
      typeId,
      spec,
      preview: root,
      previewMats: meshes.mats,
      validPoint: null,
      validRotationY: 0,
    };
  }

  stopBuildPreview() {
    const s = this._buildState;
    if (!s) return;
    if (s.preview) {
      try { s.preview.dispose(false, true); } catch (_e) { /* noop */ }
    }
    this._buildState = {
      active: false,
      typeId: null,
      spec: null,
      preview: null,
      previewMats: [],
      validPoint: null,
      validRotationY: 0,
    };
  }

  getBuildSnapshot() {
    const s = this._buildState;
    return {
      active: !!s.active,
      typeId: s.typeId,
      validPoint: s.validPoint
        ? { x: s.validPoint.x, y: s.validPoint.y, z: s.validPoint.z }
        : null,
      rotationY: s.validRotationY,
    };
  }

  /** Commit the build preview at its current valid location. Returns the
      pending placement payload (caller posts to /api/structures/build). */
  commitBuild() {
    const s = this._buildState;
    if (!s.active || !s.validPoint) return null;
    return {
      type: s.typeId,
      pos: { x: s.validPoint.x, y: s.validPoint.y, z: s.validPoint.z },
      rotation_y: s.validRotationY,
    };
  }

  // ============ Per-frame ============

  tick(_dt) {
    const s = this._buildState;
    if (!s.active || !s.preview) return;
    // Project a point in front of the player onto the terrain — use a fixed
    // 8m forward distance for predictable placement (no ray vs terrain mesh
    // needed; terrain is procedural and not collidable as a mesh).
    const fwd = this.getPlayerForward();
    const p = this.getPlayerPos();
    const target = p.add(new Vector3(fwd.x, 0, fwd.z).normalize().scale(8.0));
    const gy = this.getTerrainHeight(target.x, target.z) || 0;
    s.preview.position.set(target.x, gy, target.z);
    // Yaw toward the player so the structure faces back into the plaza
    s.preview.rotation.y = Math.atan2(p.x - target.x, p.z - target.z);
    s.validPoint = new Vector3(target.x, gy, target.z);
    s.validRotationY = s.preview.rotation.y;
  }

  // ============ Proximity / HUD ============

  /** Closest user-owned structure to the player. */
  getNearStructure(radius = 4.5) {
    const p = this.getPlayerPos();
    let best = null;
    let bestD = Infinity;
    for (const entry of this._byId.values()) {
      const dx = entry.root.position.x - p.x;
      const dz = entry.root.position.z - p.z;
      const d = Math.hypot(dx, dz);
      if (d < bestD) {
        bestD = d;
        best = entry;
      }
    }
    if (!best || bestD > radius) return null;
    return {
      id: best.doc.id,
      type: best.doc.type,
      distance: bestD,
      doc: best.doc,
    };
  }

  /** POIs for the mini-radar (beacons always; others optionally). */
  getPois() {
    const pois = [];
    for (const entry of this._byId.values()) {
      pois.push({
        id: entry.doc.id,
        kind: entry.doc.type === 'beacon' ? 'beacon' : 'structure',
        x: entry.root.position.x,
        z: entry.root.position.z,
        label: this._labelFor(entry.doc.type),
      });
    }
    return pois;
  }

  // ============ Internals ============

  _sampleY(x, z) {
    return (this.getTerrainHeight(x, z) || 0) + 0.05;
  }

  _labelFor(type) {
    const map = {
      beacon: 'Beacon',
      storage_crate: 'Crate',
      auto_miner: 'Miner',
      refuel_pad: 'Pad',
      turret: 'Turret',
      oxygen_generator: 'O₂ Gen',
      power_generator: 'Power',
      floodlight: 'Light',
      refinery: 'Refinery',
      trade_terminal: 'Trade',
      cargo_silo: 'Silo',
      heavy_turret: 'Hvy Turret',
      habitat: 'Habitat',
    };
    return map[type] || 'Structure';
  }

  /** True if the player is within an oxygen-providing structure's radius. */
  isNearOxygenSource() {
    const p = this.getPlayerPos();
    for (const entry of this._byId.values()) {
      const t = entry.doc.type;
      if (t !== 'oxygen_generator' && t !== 'habitat') continue;
      const r = Number(entry.doc?.data?.radius || (t === 'habitat' ? 9 : 16));
      const dx = entry.root.position.x - p.x;
      const dz = entry.root.position.z - p.z;
      if (Math.hypot(dx, dz) <= r) return true;
    }
    return false;
  }

  _specFor(typeId) {
    const color = TYPE_COLOR[typeId] || '#ffffff';
    switch (typeId) {
      case 'beacon':
        return { label: 'Beacon', footprint: 1.2, height: 12, color, shape: 'pylon' };
      case 'storage_crate':
        return { label: 'Storage Crate', footprint: 2.2, height: 2.0, color, shape: 'box' };
      case 'auto_miner':
        return { label: 'Auto-Miner', footprint: 2.4, height: 2.8, color, shape: 'drill' };
      case 'refuel_pad':
        return { label: 'Refuel Pad', footprint: 4.0, height: 0.6, color, shape: 'pad' };
      case 'turret':
        return { label: 'Turret', footprint: 1.6, height: 3.0, color, shape: 'turret' };
      case 'oxygen_generator':
        return { label: 'Oxygen Generator', footprint: 2.0, height: 3.2, color, shape: 'oxygen' };
      case 'power_generator':
        return { label: 'Power Generator', footprint: 2.6, height: 2.6, color, shape: 'power' };
      case 'floodlight':
        return { label: 'Floodlight Tower', footprint: 1.0, height: 6.5, color, shape: 'floodlight' };
      case 'refinery':
        return { label: 'Refinery', footprint: 3.2, height: 4.2, color, shape: 'refinery' };
      case 'trade_terminal':
        return { label: 'Trade Terminal', footprint: 1.8, height: 2.6, color, shape: 'terminal' };
      case 'cargo_silo':
        return { label: 'Cargo Silo', footprint: 3.0, height: 5.0, color, shape: 'silo' };
      case 'heavy_turret':
        return { label: 'Heavy Turret', footprint: 2.4, height: 4.0, color, shape: 'heavy_turret' };
      case 'habitat':
        return { label: 'Habitat Module', footprint: 5.0, height: 4.0, color, shape: 'habitat' };
      default:
        return null;
    }
  }

  _buildStructureMesh(doc) {
    const spec = this._specFor(doc.type);
    const root = new TransformNode(`structure_${doc.id}`, this.scene);
    const wx = doc.pos?.x ?? 0;
    const wz = doc.pos?.z ?? 0;
    const wy = this._sampleY(wx, wz);
    root.position.set(wx, wy, wz);
    root.rotation.y = doc.rotation_y || 0;
    const { meshes, mats } = this._spawnSilhouette(spec, root, /* preview */ false, doc);
    return { doc, root, meshes, mats };
  }

  _spawnSilhouette(spec, parent, preview, doc = null) {
    const meshes = [];
    const mats = [];
    const color = hex(spec.color);
    const accent = new Color3(0.92, 0.85, 0.55);
    const previewAlpha = preview ? 0.45 : 1.0;
    const makeMat = (name, c, emissiveBoost = 0.0) => {
      const m = new StandardMaterial(name, this.scene);
      m.diffuseColor = c.clone();
      m.specularColor = new Color3(0, 0, 0);
      m.emissiveColor = c.scale(emissiveBoost);
      m.alpha = previewAlpha;
      mats.push(m);
      return m;
    };

    if (spec.shape === 'pylon') {
      const pole = MeshBuilder.CreateCylinder('beaconPole', { diameter: 0.45, height: spec.height }, this.scene);
      pole.position.set(0, spec.height / 2, 0);
      pole.material = makeMat('beaconPoleMat', new Color3(0.12, 0.14, 0.18), 0.0);
      pole.parent = parent;
      meshes.push(pole);
      const orb = MeshBuilder.CreateSphere('beaconOrb', { diameter: 1.1, segments: 12 }, this.scene);
      orb.position.set(0, spec.height + 0.4, 0);
      orb.material = makeMat('beaconOrbMat', color, 1.4);
      orb.parent = parent;
      meshes.push(orb);
    } else if (spec.shape === 'box') {
      const box = MeshBuilder.CreateBox('crateBox', { width: spec.footprint, depth: spec.footprint, height: spec.height }, this.scene);
      box.position.set(0, spec.height / 2, 0);
      box.material = makeMat('crateBoxMat', color, 0.05);
      box.parent = parent;
      meshes.push(box);
      const strip = MeshBuilder.CreateBox('crateStrip', { width: spec.footprint * 0.92, depth: spec.footprint * 0.92, height: 0.18 }, this.scene);
      strip.position.set(0, spec.height * 0.7, 0);
      strip.material = makeMat('crateStripMat', accent, 0.9);
      strip.parent = parent;
      meshes.push(strip);
    } else if (spec.shape === 'drill') {
      const base = MeshBuilder.CreateBox('minerBase', { width: spec.footprint, depth: spec.footprint, height: 1.2 }, this.scene);
      base.position.set(0, 0.6, 0);
      base.material = makeMat('minerBaseMat', color.scale(0.6), 0.0);
      base.parent = parent;
      meshes.push(base);
      const drill = MeshBuilder.CreateCylinder('minerDrill', { diameterTop: 0.1, diameterBottom: 1.0, height: spec.height - 1.2, tessellation: 12 }, this.scene);
      drill.position.set(0, 1.2 + (spec.height - 1.2) / 2, 0);
      drill.material = makeMat('minerDrillMat', color, 0.4);
      drill.parent = parent;
      meshes.push(drill);
    } else if (spec.shape === 'pad') {
      const pad = MeshBuilder.CreateCylinder('padDisc', { diameter: spec.footprint * 2.0, height: spec.height, tessellation: 32 }, this.scene);
      pad.position.set(0, spec.height / 2, 0);
      pad.material = makeMat('padDiscMat', color.scale(0.4), 0.0);
      pad.parent = parent;
      meshes.push(pad);
      const ring = MeshBuilder.CreateTorus('padRing', { diameter: spec.footprint * 1.9, thickness: 0.18, tessellation: 48 }, this.scene);
      ring.position.set(0, spec.height + 0.2, 0);
      ring.material = makeMat('padRingMat', color, 1.3);
      ring.parent = parent;
      meshes.push(ring);
    } else if (spec.shape === 'turret') {
      const base = MeshBuilder.CreateCylinder('turretBase', { diameter: spec.footprint, height: 0.4, tessellation: 16 }, this.scene);
      base.position.set(0, 0.2, 0);
      base.material = makeMat('turretBaseMat', new Color3(0.12, 0.13, 0.18), 0.0);
      base.parent = parent;
      meshes.push(base);
      const post = MeshBuilder.CreateCylinder('turretPost', { diameter: 0.55, height: spec.height - 1.4, tessellation: 12 }, this.scene);
      post.position.set(0, 0.4 + (spec.height - 1.4) / 2, 0);
      post.material = makeMat('turretPostMat', new Color3(0.18, 0.2, 0.24), 0.0);
      post.parent = parent;
      meshes.push(post);
      const head = MeshBuilder.CreateBox('turretHead', { width: 1.2, depth: 1.6, height: 0.8 }, this.scene);
      head.position.set(0, spec.height - 0.6, 0);
      head.material = makeMat('turretHeadMat', color, 0.4);
      head.parent = parent;
      meshes.push(head);
      const barrel = MeshBuilder.CreateCylinder('turretBarrel', { diameter: 0.22, height: 1.4, tessellation: 12 }, this.scene);
      barrel.position.set(0, spec.height - 0.5, 0.9);
      barrel.rotation.x = Math.PI / 2;
      barrel.material = makeMat('turretBarrelMat', accent, 0.6);
      barrel.parent = parent;
      meshes.push(barrel);
    } else if (spec.shape === 'oxygen') {
      const tank = MeshBuilder.CreateCylinder('oxyTank', { diameter: spec.footprint, height: spec.height, tessellation: 20 }, this.scene);
      tank.position.set(0, spec.height / 2, 0);
      tank.material = makeMat('oxyTankMat', new Color3(0.85, 0.92, 0.98), 0.06);
      tank.parent = parent;
      meshes.push(tank);
      const cap = MeshBuilder.CreateSphere('oxyCap', { diameter: spec.footprint, segments: 14 }, this.scene);
      cap.position.set(0, spec.height, 0);
      cap.scaling.y = 0.5;
      cap.material = makeMat('oxyCapMat', color, 0.9);
      cap.parent = parent;
      meshes.push(cap);
      const ring = MeshBuilder.CreateTorus('oxyRing', { diameter: spec.footprint * 1.5, thickness: 0.12, tessellation: 40 }, this.scene);
      ring.position.set(0, 0.4, 0);
      ring.material = makeMat('oxyRingMat', color, 1.2);
      ring.parent = parent;
      meshes.push(ring);
    } else if (spec.shape === 'power') {
      const base = MeshBuilder.CreateBox('pwrBase', { width: 1.0, depth: 1.0, height: 1.4 }, this.scene);
      base.position.set(0, 0.7, 0);
      base.material = makeMat('pwrBaseMat', new Color3(0.18, 0.2, 0.24), 0.0);
      base.parent = parent;
      meshes.push(base);
      const panel = MeshBuilder.CreateBox('pwrPanel', { width: spec.footprint, depth: 0.12, height: spec.footprint * 0.7 }, this.scene);
      panel.position.set(0, spec.height, 0);
      panel.rotation.x = -Math.PI / 4;
      panel.material = makeMat('pwrPanelMat', new Color3(0.08, 0.12, 0.32), 0.0);
      panel.parent = parent;
      meshes.push(panel);
      const glow = MeshBuilder.CreateBox('pwrGlow', { width: spec.footprint * 0.9, depth: 0.05, height: spec.footprint * 0.6 }, this.scene);
      glow.position.set(0, spec.height + 0.02, 0.02);
      glow.rotation.x = -Math.PI / 4;
      glow.material = makeMat('pwrGlowMat', color, 0.7);
      glow.parent = parent;
      meshes.push(glow);
    } else if (spec.shape === 'floodlight') {
      const pole = MeshBuilder.CreateCylinder('floodPole', { diameter: 0.35, height: spec.height }, this.scene);
      pole.position.set(0, spec.height / 2, 0);
      pole.material = makeMat('floodPoleMat', new Color3(0.16, 0.17, 0.2), 0.0);
      pole.parent = parent;
      meshes.push(pole);
      // Head tilted to face the ground in front of the tower.
      const head = MeshBuilder.CreateBox('floodHead', { width: 1.4, depth: 0.9, height: 0.6 }, this.scene);
      head.position.set(0, spec.height - 0.2, 0.5);
      head.rotation.x = 0.6;
      head.material = makeMat('floodHeadMat', color, 2.2);
      head.parent = parent;
      meshes.push(head);
      // Bright glowing lens so it reads as "lit" even in daylight.
      const lens = MeshBuilder.CreateDisc('floodLens', { radius: 0.5, tessellation: 24 }, this.scene);
      lens.position.set(0, spec.height - 0.35, 0.95);
      lens.rotation.x = -Math.PI / 2 + 0.6;
      const lensMat = makeMat('floodLensMat', new Color3(1, 0.97, 0.85), 2.6);
      lensMat.disableLighting = true;
      lens.material = lensMat;
      lens.parent = parent;
      meshes.push(lens);
      if (!preview) {
        // Visible warm pool of light on the ground (always shows; sells the
        // "illumination" read regardless of time-of-day / light budget).
        const pool = MeshBuilder.CreateDisc('floodPool', { radius: 9, tessellation: 40 }, this.scene);
        pool.rotation.x = Math.PI / 2;
        pool.position.set(0, 0.08, 4);
        const poolMat = new StandardMaterial('floodPoolMat', this.scene);
        poolMat.emissiveColor = new Color3(1, 0.93, 0.72);
        poolMat.diffuseColor = new Color3(0, 0, 0);
        poolMat.specularColor = new Color3(0, 0, 0);
        poolMat.disableLighting = true;
        poolMat.alpha = 0.22;
        poolMat.backFaceCulling = false;
        pool.material = poolMat;
        pool.isPickable = false;
        pool.parent = parent;
        meshes.push(pool);
        mats.push(poolMat);
        // Real downward spotlight that actually lights the terrain & objects.
        const lamp = new SpotLight(
          `floodLamp_${parent.name}`,
          new Vector3(0, spec.height - 0.2, 0.5),
          new Vector3(0, -1, 0.35),
          Math.PI / 2.4,
          2,
          this.scene,
        );
        lamp.diffuse = new Color3(1, 0.95, 0.82);
        lamp.intensity = 7.0;
        lamp.range = 48;
        lamp.parent = parent;
        meshes.push(lamp);
      }
    } else if (spec.shape === 'refinery') {
      const body = MeshBuilder.CreateBox('refBody', { width: spec.footprint, depth: spec.footprint, height: 2.4 }, this.scene);
      body.position.set(0, 1.2, 0);
      body.material = makeMat('refBodyMat', new Color3(0.22, 0.2, 0.18), 0.0);
      body.parent = parent;
      meshes.push(body);
      const stack1 = MeshBuilder.CreateCylinder('refStack1', { diameter: 0.7, height: spec.height - 2.4 }, this.scene);
      stack1.position.set(spec.footprint * 0.28, 2.4 + (spec.height - 2.4) / 2, 0);
      stack1.material = makeMat('refStackMat', color.scale(0.8), 0.2);
      stack1.parent = parent;
      meshes.push(stack1);
      const stack2 = MeshBuilder.CreateCylinder('refStack2', { diameter: 0.55, height: (spec.height - 2.4) * 0.7 }, this.scene);
      stack2.position.set(-spec.footprint * 0.28, 2.4 + (spec.height - 2.4) * 0.35, 0);
      stack2.material = makeMat('refStack2Mat', color, 0.4);
      stack2.parent = parent;
      meshes.push(stack2);
    } else if (spec.shape === 'terminal') {
      const post = MeshBuilder.CreateBox('termPost', { width: 0.4, depth: 0.4, height: spec.height - 0.8 }, this.scene);
      post.position.set(0, (spec.height - 0.8) / 2, 0);
      post.material = makeMat('termPostMat', new Color3(0.18, 0.18, 0.24), 0.0);
      post.parent = parent;
      meshes.push(post);
      const screen = MeshBuilder.CreateBox('termScreen', { width: spec.footprint, depth: 0.18, height: 1.3 }, this.scene);
      screen.position.set(0, spec.height - 0.6, 0);
      screen.material = makeMat('termScreenMat', color, 1.1);
      screen.parent = parent;
      meshes.push(screen);
    } else if (spec.shape === 'silo') {
      const drum = MeshBuilder.CreateCylinder('siloDrum', { diameter: spec.footprint, height: spec.height, tessellation: 24 }, this.scene);
      drum.position.set(0, spec.height / 2, 0);
      drum.material = makeMat('siloDrumMat', color, 0.04);
      drum.parent = parent;
      meshes.push(drum);
      const dome = MeshBuilder.CreateSphere('siloDome', { diameter: spec.footprint, segments: 16 }, this.scene);
      dome.position.set(0, spec.height, 0);
      dome.scaling.y = 0.45;
      dome.material = makeMat('siloDomeMat', color.scale(0.7), 0.06);
      dome.parent = parent;
      meshes.push(dome);
      const band = MeshBuilder.CreateTorus('siloBand', { diameter: spec.footprint, thickness: 0.16, tessellation: 32 }, this.scene);
      band.position.set(0, spec.height * 0.6, 0);
      band.material = makeMat('siloBandMat', accent, 0.7);
      band.parent = parent;
      meshes.push(band);
    } else if (spec.shape === 'heavy_turret') {
      const base = MeshBuilder.CreateCylinder('hTurBase', { diameter: spec.footprint, height: 0.6, tessellation: 18 }, this.scene);
      base.position.set(0, 0.3, 0);
      base.material = makeMat('hTurBaseMat', new Color3(0.1, 0.11, 0.15), 0.0);
      base.parent = parent;
      meshes.push(base);
      const post = MeshBuilder.CreateCylinder('hTurPost', { diameter: 0.8, height: spec.height - 1.8, tessellation: 14 }, this.scene);
      post.position.set(0, 0.6 + (spec.height - 1.8) / 2, 0);
      post.material = makeMat('hTurPostMat', new Color3(0.16, 0.18, 0.22), 0.0);
      post.parent = parent;
      meshes.push(post);
      const head = MeshBuilder.CreateBox('hTurHead', { width: 1.8, depth: 2.2, height: 1.1 }, this.scene);
      head.position.set(0, spec.height - 0.8, 0);
      head.material = makeMat('hTurHeadMat', color, 0.4);
      head.parent = parent;
      meshes.push(head);
      for (const dx of [-0.45, 0.45]) {
        const barrel = MeshBuilder.CreateCylinder(`hTurBarrel${dx}`, { diameter: 0.28, height: 2.0, tessellation: 12 }, this.scene);
        barrel.position.set(dx, spec.height - 0.7, 1.2);
        barrel.rotation.x = Math.PI / 2;
        barrel.material = makeMat(`hTurBarrelMat${dx}`, accent, 0.6);
        barrel.parent = parent;
        meshes.push(barrel);
      }
    } else if (spec.shape === 'habitat') {
      const half = spec.footprint;        // half-width of the room
      const wallH = spec.height;
      const t = 0.4;                       // wall thickness
      const doorW = 2.0;                   // doorway width
      const wallMat = makeMat('habWallMat', new Color3(0.32, 0.4, 0.45), 0.05);
      const floor = MeshBuilder.CreateBox('habFloor', { width: half * 2, depth: half * 2, height: 0.3 }, this.scene);
      floor.position.set(0, 0.15, 0);
      floor.material = makeMat('habFloorMat', new Color3(0.2, 0.24, 0.28), 0.02);
      floor.parent = parent;
      meshes.push(floor);
      // Back wall (+Z), left (-X), right (+X) full; front (-Z) split for doorway.
      const back = MeshBuilder.CreateBox('habBack', { width: half * 2, depth: t, height: wallH }, this.scene);
      back.position.set(0, wallH / 2, half);
      back.material = wallMat; back.parent = parent; meshes.push(back);
      const left = MeshBuilder.CreateBox('habLeft', { width: t, depth: half * 2, height: wallH }, this.scene);
      left.position.set(-half, wallH / 2, 0);
      left.material = wallMat; left.parent = parent; meshes.push(left);
      const right = MeshBuilder.CreateBox('habRight', { width: t, depth: half * 2, height: wallH }, this.scene);
      right.position.set(half, wallH / 2, 0);
      right.material = wallMat; right.parent = parent; meshes.push(right);
      const sideW = (half * 2 - doorW) / 2;
      for (const sx of [-1, 1]) {
        const seg = MeshBuilder.CreateBox(`habFront${sx}`, { width: sideW, depth: t, height: wallH }, this.scene);
        seg.position.set(sx * (doorW / 2 + sideW / 2), wallH / 2, -half);
        seg.material = wallMat; seg.parent = parent; meshes.push(seg);
      }
      const lintel = MeshBuilder.CreateBox('habLintel', { width: doorW, depth: t, height: wallH - 2.4 }, this.scene);
      lintel.position.set(0, wallH - (wallH - 2.4) / 2, -half);
      lintel.material = wallMat; lintel.parent = parent; meshes.push(lintel);
      const roof = MeshBuilder.CreateBox('habRoof', { width: half * 2 + 0.4, depth: half * 2 + 0.4, height: 0.4 }, this.scene);
      roof.position.set(0, wallH, 0);
      roof.material = makeMat('habRoofMat', color.scale(0.5), 0.06);
      roof.parent = parent; meshes.push(roof);
      const rim = MeshBuilder.CreateTorus('habRim', { diameter: doorW + 0.6, thickness: 0.14, tessellation: 24 }, this.scene);
      rim.position.set(0, 1.2, -half);
      rim.rotation.x = Math.PI / 2;
      rim.material = makeMat('habRimMat', color, 1.3);
      rim.parent = parent; meshes.push(rim);
      // Glowing exterior light strips along the roof edges (front + back).
      const stripMat = makeMat('habStripMat', color, 1.8);
      stripMat.disableLighting = true;
      for (const sz of [-1, 1]) {
        const strip = MeshBuilder.CreateBox(`habStrip${sz}`, { width: half * 2, depth: 0.12, height: 0.18 }, this.scene);
        strip.position.set(0, wallH - 0.25, sz * (half - 0.05));
        strip.material = stripMat;
        strip.parent = parent; meshes.push(strip);
      }
      // Exterior corner lamp fixtures (small bright emissive spheres).
      const lampFixMat = makeMat('habLampFixMat', new Color3(1, 0.95, 0.8), 2.4);
      lampFixMat.disableLighting = true;
      for (const cx of [-1, 1]) {
        const fix = MeshBuilder.CreateSphere(`habFix${cx}`, { diameter: 0.4, segments: 8 }, this.scene);
        fix.position.set(cx * (half - 0.2), wallH - 0.5, -half + 0.2);
        fix.material = lampFixMat;
        fix.parent = parent; meshes.push(fix);
      }
      // Interior ceiling lamp panel.
      const ceil = MeshBuilder.CreateBox('habCeilLamp', { width: 1.8, depth: 1.8, height: 0.12 }, this.scene);
      ceil.position.set(0, wallH - 0.5, 0);
      const ceilMat = makeMat('habCeilMat', new Color3(1, 0.97, 0.88), 2.2);
      ceilMat.disableLighting = true;
      ceil.material = ceilMat;
      ceil.parent = parent; meshes.push(ceil);
      if (!preview) {
        // Bright interior light so the room is lit when you step inside.
        const inner = new PointLight(`habInner_${parent.name}`, new Vector3(0, wallH - 0.7, 0), this.scene);
        inner.diffuse = new Color3(1, 0.96, 0.86);
        inner.intensity = 1.8;
        inner.range = half * 3.0;
        inner.parent = parent;
        meshes.push(inner);
        // Exterior entry light over the doorway.
        const entry = new PointLight(`habEntry_${parent.name}`, new Vector3(0, wallH - 0.6, -half - 0.6), this.scene);
        entry.diffuse = color.clone();
        entry.intensity = 1.4;
        entry.range = half * 2.2;
        entry.parent = parent;
        meshes.push(entry);
      }
    }

    // Mark preview meshes so they don't pick on placement
    if (preview) {
      meshes.forEach((m) => { m.isPickable = false; });
    }

    void doc; // doc currently unused for visuals
    return { meshes, mats };
  }

  dispose() {
    this.clear();
    try { this._root.dispose(false, true); } catch (_e) { /* noop */ }
  }
}

export default StructuresSystem;
